export interface GroupMember {
  value: string;
  memberAttribute: string;
  members: ProductGroupMember[];
  order?: number;
}
export interface ProductMember {
  value: string;
  sku: string;
  slug?: string;
  name?: string;
  order?: number;
}
export type ProductGroupMember = GroupMember | ProductMember;

export interface ProductGroup {
  groupId: string;
  groupName: string;
  memberAttribute: string;
  members: ProductGroupMember[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductGroupMutationResult extends ProductGroup {
  failedSkus: string[];
}

export interface CreateProductGroupPayload {
  groupName: string;
  memberAttribute: string;
  members: ProductGroupMember[];
}

export interface UpdateProductGroupPayload extends CreateProductGroupPayload {}

export interface ListProductGroupsResponse {
  productGroups: ProductGroup[];
  nextCursor?: string;
}

export type BulkCreateStatus = 'created' | 'failed';

export interface BulkCreatedProductGroup {
  groupName: string;
  status: BulkCreateStatus;
  groupId?: string;
  failedSkus: string[];
  error?: string;
}

export interface BulkCreateProductGroupsResponse {
  productGroups: BulkCreatedProductGroup[];
  createdCount: number;
  failedCount: number;
}

export interface BulkCreateProductGroupEntryPayload {
  groupName: string;
  memberAttribute: string;
  members: ProductGroupMember[];
}

export const isProductMember = (member: ProductGroupMember): member is ProductMember => 'sku' in member;
export const isGroupMember = (member: ProductGroupMember): member is GroupMember => 'members' in member;

export class ProductGroupClient {
  private readonly headers: Record<string, string>;

  constructor(
    private readonly apiUrl: string,
    apiKey: string,
  ) {
    this.headers = {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    };
  }

  public async list(params?: { limit?: number; cursor?: string }): Promise<ListProductGroupsResponse> {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', String(params.limit));
    if (params?.cursor) query.append('cursor', params.cursor);
    const url = query.toString() ? `${this.apiUrl}/productGroups?${query}` : `${this.apiUrl}/productGroups`;
    const response = await fetch(url, { headers: this.headers });
    if (!response.ok) throw new Error(await this.readError(response, 'Failed to list product groups'));
    return response.json();
  }

  public async get(groupId: string): Promise<ProductGroup> {
    const response = await fetch(`${this.apiUrl}/productGroups/${groupId}`, { headers: this.headers });
    if (!response.ok) throw new Error(await this.readError(response, `Failed to fetch product group ${groupId}`));
    return response.json();
  }

  public async create(payload: CreateProductGroupPayload): Promise<ProductGroupMutationResult> {
    const response = await fetch(`${this.apiUrl}/productGroups`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await this.readError(response, 'Failed to create product group'));
    return response.json();
  }

  public async update(groupId: string, payload: UpdateProductGroupPayload): Promise<ProductGroupMutationResult> {
    const response = await fetch(`${this.apiUrl}/productGroups/${groupId}`, {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await this.readError(response, `Failed to update product group ${groupId}`));
    return response.json();
  }

  public async delete(groupId: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/productGroups/${groupId}`, {
      method: 'DELETE',
      headers: this.headers,
    });
    if (!response.ok) throw new Error(await this.readError(response, `Failed to delete product group ${groupId}`));
  }

  public async bulkCreate(groups: BulkCreateProductGroupEntryPayload[]): Promise<BulkCreateProductGroupsResponse> {
    const response = await fetch(`${this.apiUrl}/productGroups/bulkCreate`, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ groups }),
    });
    if (!response.ok) throw new Error(await this.readError(response, 'Failed to bulk create product groups'));
    return response.json();
  }

  private async readError(response: Response, fallback: string): Promise<string> {
    try {
      const body = await response.json();
      if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message);
    } catch {
      // body wasn't JSON
    }
    return `${fallback} (HTTP ${response.status})`;
  }
}
